import { enhanceSidebars, registerSidebar } from './sidebar';
import { registerBirthDate } from './dob';
import { registerProgramConversion } from './program-conversion';
import { registerSingleSelect, enhanceSingleSelects, resetSelectClones } from './single-select';
import type { Alpine as AlpineAPI } from 'alpinejs';
import { enhanceTables } from './tables';
import { enhanceAppearance } from './appearance';
import { registerFileUpload } from './file-upload';
import { registerEvidencePreview } from './evidence-preview';
import { registerMultiselect, enhanceMultiselects } from './multiselect';

declare global {
    interface Window {
        Alpine: AlpineAPI;
        AB4HUI: typeof UI;
    }
}

type Target = string | Element | null | undefined;
const element = (target: Target) => typeof target === 'string' ? document.querySelector<HTMLElement>(target) : target instanceof HTMLElement ? target : null;
const tabControllers = new WeakMap<Element, (id: string) => void>();
const initialized = new WeakSet<Element>();
const measuredHeaders = new WeakSet<Element>();
const returnFocus = new WeakMap<Element, HTMLElement>();
let sequence = 0;
let lastTrigger: HTMLElement | null = null;
const dialogs = { active: [] as string[] };
const dialogState = () => window.Alpine.store('dialogs') as typeof dialogs;

/** Public methods used by PHP templates; no jQuery/Foundation adapter is required. */
const UI = {
    open(target: Target) {
        let node = element(target);
        if (!node) return;
        enhance(node);
        node = document.getElementById(node.id)!;
        const focused = document.activeElement;
        const opener = focused instanceof HTMLElement && focused !== document.body ? focused : lastTrigger;
        if (opener) returnFocus.set(node, opener);
        const state = dialogState();
        if (!state.active.includes(node.id)) state.active.push(node.id);
    },
    close(target: Target) {
        const node = element(target);
        if (!node) return;
        const state = dialogState();
        if (!state.active.includes(node.id)) return;
        state.active = state.active.filter(id => id !== node.id);
        node.dispatchEvent(new CustomEvent('ab4h:dialog-closed', { bubbles: true }));
        window.Alpine.nextTick(() => returnFocus.get(node)?.focus());
    },
    selectTab(target: Target, panel: string) {
        const node = element(target);
        if (node) tabControllers.get(node)?.(panel.replace(/^#/, ''));
    },
    enhance,
    prepareClone: resetSelectClones,
};
window.AB4HUI = UI;

function matches(root: ParentNode, selector: string): HTMLElement[] {
    return [...(root instanceof HTMLElement && root.matches(selector) ? [root] : []), ...root.querySelectorAll<HTMLElement>(selector)];
}

/** Attach Alpine directives before startup, and to fragments inserted by legacy forms. */
function enhance(root: ParentNode = document) {
    for (const header of matches(root, '.ab4h-top-bar')) {
        if (measuredHeaders.has(header)) continue;
        measuredHeaders.add(header);
        // Navigation wraps differently with permissions, viewport width and text zoom.
        const measure = () => document.documentElement.style.setProperty('--ab-header-height', `${header.getBoundingClientRect().height}px`);
        measure();
        new ResizeObserver(measure).observe(header);
    }
    enhanceSidebars(root);
    enhanceAppearance(root);
    enhanceMultiselects(root);
    enhanceSingleSelects(root);
    for (let node of matches(root, '[data-dialog], [data-ui-tabs], [data-ui-responsive-tabs], [data-ui-accordion], [data-ui-tooltip]')) {
        if (initialized.has(node)) continue;
        if (!node.id) node.id = `ab4h-ui-${++sequence}`;
        if (!node.hasAttribute('x-data')) node.setAttribute('x-data', '{}');
        if (node.hasAttribute('data-dialog')) {
            // Native dialogs enter the browser's top layer without leaving their Alpine scope.
            if (!(node instanceof HTMLDialogElement)) {
                const dialog = document.createElement('dialog');
                for (const attribute of node.attributes) dialog.setAttribute(attribute.name, attribute.value);
                dialog.append(...node.childNodes);
                node.replaceWith(dialog);
                node = dialog;
            }
            node.classList.add('dui-modal-box');
            node.setAttribute('x-ab-dialog', '');
            node.setAttribute('x-trap.noautofocus.noreturn', '$store.dialogs.active.at(-1) === $el.id');
        } else if (node.hasAttribute('data-ui-accordion')) node.setAttribute('x-ab-accordion', '');
        else if (node.hasAttribute('data-ui-tooltip')) node.setAttribute('x-ab-tooltip', '');
        else node.setAttribute('x-ab-tabs', '');
        initialized.add(node);
    }
    for (const node of matches(root, '[data-ui-sticky]')) {
        node.style.setProperty('--sticky-top', `max(${Number(node.dataset.marginTop || 1)}rem, calc(var(--ab-header-height, 0px) + 1rem))`);
    }
    enhanceTables(root);
}

document.addEventListener('alpine:init', () => {
    const Alpine = window.Alpine;
    Alpine.store('dialogs', dialogs);
    registerSidebar(Alpine);
    registerBirthDate(Alpine);
    registerProgramConversion(Alpine);
    registerMultiselect(Alpine);
    registerSingleSelect(Alpine);
    registerFileUpload(Alpine);
    registerEvidencePreview(Alpine);
    document.addEventListener('click', event => {
        if (event.target instanceof Element) lastTrigger = event.target.closest<HTMLElement>('button, a, input[type=submit], [role=button]');
    }, true);
    Alpine.directive('ab-dialog', (el, _directive, { effect, cleanup }) => {
        if (!(el instanceof HTMLDialogElement)) return;
        el.setAttribute('role', 'dialog');
        el.setAttribute('aria-modal', 'true');
        el.tabIndex = -1;
        // Older templates used human-readable titles as aria-labelledby values.
        const labelledby = el.getAttribute('aria-labelledby');
        if (labelledby && !document.getElementById(labelledby)) {
            el.setAttribute('aria-label', labelledby);
            el.removeAttribute('aria-labelledby');
        }
        effect(() => {
            const open = dialogState().active.at(-1) === el.id;
            el.classList.toggle('is-open', open);
            el.setAttribute('aria-hidden', String(!open));
            if (open && !el.open) el.showModal();
            if (!open && el.open) el.close();
            document.documentElement.classList.toggle('ab-dialog-open', dialogState().active.length > 0);
            if (open) Alpine.nextTick(() => el.focus());
        });
        const cancel = (event: Event) => { event.preventDefault(); UI.close(el); };
        let pressedBackdrop = false;
        const pointerdown = (event: PointerEvent) => { pressedBackdrop = event.target === el; };
        const outside = (event: MouseEvent) => {
            // A picker can disappear on mousedown, retargeting its click to the dialog.
            const startedOnBackdrop = pressedBackdrop;
            pressedBackdrop = false;
            if (!startedOnBackdrop || event.target !== el) return;
            const rect = el.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) UI.close(el);
        };
        el.addEventListener('cancel', cancel);
        el.addEventListener('pointerdown', pointerdown);
        el.addEventListener('click', outside);
        cleanup(() => { el.removeEventListener('cancel', cancel); el.removeEventListener('pointerdown', pointerdown); el.removeEventListener('click', outside); });
        if (el.classList.contains('auto-open')) Alpine.nextTick(() => UI.open(el));
    });

    Alpine.directive('ab-tabs', (el, _directive, { cleanup }) => {
        // Route-backed tabs keep real navigation; panel tabs switch content in place.
        const navigation = el.dataset.uiTabs === 'navigation';
        const links = [...el.querySelectorAll<HTMLAnchorElement>(navigation ? ':scope > li > a[href]' : ':scope > li > a[href^="#"]')];
        const panels = links.map(link => navigation ? null : document.getElementById(link.hash.slice(1)));
        const strip = document.createElement('div');
        strip.className = 'ab-tab-strip';
        const arrows = [-1, 1].map(direction => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'ab-tab-scroll dui-btn dui-btn-outline';
            button.textContent = direction < 0 ? '‹' : '›';
            button.setAttribute('aria-label', `Scroll tabs ${direction < 0 ? 'left' : 'right'}`);
            button.setAttribute('aria-controls', el.id);
            button.hidden = true;
            button.addEventListener('click', () => el.scrollBy({ left: direction * el.clientWidth * .7 }));
            return button;
        });
        // Keep the original links and panels so form values and Alpine scopes survive.
        Alpine.mutateDom(() => {
            el.before(strip);
            strip.append(arrows[0]!, el, arrows[1]!);
        });
        el.hidden = false;
        el.classList.add('ab-connected-tabs');
        function updateOverflow() {
            const overflowing = el.scrollWidth > strip.clientWidth + 1;
            arrows.forEach(button => button.hidden = !overflowing);
            arrows[0]!.disabled = el.scrollLeft <= 1;
            arrows[1]!.disabled = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
        }
        function revealActive(index = active) {
            const link = links[index];
            if (!link) return;
            const rect = link.getBoundingClientRect();
            const viewport = el.getBoundingClientRect();
            if (rect.left < viewport.left) el.scrollLeft += rect.left - viewport.left;
            else if (rect.right > viewport.right) el.scrollLeft += rect.right - viewport.right;
        }
        let active = Math.max(0, links.findIndex(link => link.parentElement?.classList.contains('is-active')));
        el.setAttribute('role', navigation ? 'navigation' : 'tablist');
        function select(index: number, notify = true) {
            active = index;
            links.forEach((link, i) => {
                const selected = i === active;
                link.parentElement?.classList.toggle('is-active', selected);
                link.classList.toggle('dui-tab-active', selected);
                if (navigation) {
                    if (selected) link.setAttribute('aria-current', 'page');
                    else link.removeAttribute('aria-current');
                    return;
                }
                link.setAttribute('role', 'tab');
                link.setAttribute('aria-selected', String(selected));
                link.setAttribute('aria-controls', link.hash.slice(1));
                link.tabIndex = selected ? 0 : -1;
                if (!link.id) link.id = `${el.id}-tab-${i}`;
                const panel = panels[i];
                if (panel) {
                    panel.classList.toggle('is-active', selected);
                    panel.setAttribute('role', 'tabpanel');
                    panel.setAttribute('aria-labelledby', link.id);
                    panel.hidden = !selected;
                }
            });
            updateOverflow();
            revealActive();
            updateOverflow();
            if (navigation || !notify || index < 0) return;
            const panel = panels[index];
            const key = panel?.id.replace(/^panel_/, '');
            if (key) {
                const input = document.querySelector<HTMLInputElement>('#tab');
                if (input) input.value = key;
                const url = new URL(location.href);
                url.searchParams.set('tab', key);
                history.replaceState(history.state, '', url);
            }
            el.dispatchEvent(new CustomEvent('ab4h:tab-changed', { bubbles: true, detail: { panel } }));
        }
        const click = (event: MouseEvent) => {
            if (navigation) return;
            const link = (event.target as Element).closest('a');
            const index = links.indexOf(link as HTMLAnchorElement);
            if (index < 0) return;
            event.preventDefault();
            select(index);
        };
        const keydown = (event: KeyboardEvent) => {
            const current = links.indexOf(document.activeElement as HTMLAnchorElement);
            if (current < 0 || !['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const index = event.key === 'Home' ? 0 : event.key === 'End' ? links.length - 1 : (current + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + links.length) % links.length;
            if (!navigation) select(index);
            links[index]?.focus({ preventScroll: true });
            if (navigation) { revealActive(index); updateOverflow(); }
        };
        if (!navigation) tabControllers.set(el, id => { const index = panels.findIndex(p => p?.id === id); if (index >= 0) select(index); });
        el.addEventListener('click', click);
        el.addEventListener('keydown', keydown);
        el.addEventListener('scroll', updateOverflow, { passive: true });
        const resize = new ResizeObserver(() => {
            updateOverflow();
            revealActive();
            updateOverflow();
        });
        resize.observe(strip);
        select(active, false);
        cleanup(() => {
            el.removeEventListener('click', click);
            el.removeEventListener('keydown', keydown);
            el.removeEventListener('scroll', updateOverflow);
            resize.disconnect();
            Alpine.mutateDom(() => { strip.replaceWith(el); });
            tabControllers.delete(el);
        });
    });

    Alpine.directive('ab-accordion', (el, _directive, { cleanup }) => {
        const items = [...el.querySelectorAll<HTMLElement>(':scope > .accordion-item')];
        const sync = () => items.forEach((item, i) => {
            const title = item.querySelector<HTMLElement>(':scope > .accordion-title');
            const panel = item.querySelector<HTMLElement>(':scope > .accordion-content');
            if (!title || !panel) return;
            if (!panel.id) panel.id = `${el.id}-panel-${i}`;
            const open = item.classList.contains('is-active');
            item.classList.toggle('dui-collapse-open', open);
            title.setAttribute('role', 'button');
            title.setAttribute('aria-expanded', String(open));
            title.setAttribute('aria-controls', panel.id);
            title.tabIndex = 0;
            panel.hidden = !open;
            panel.style.display = open ? 'block' : 'none';
        });
        const click = (event: MouseEvent | KeyboardEvent) => {
            const title = (event.target as Element).closest('.accordion-title');
            const item = title?.parentElement;
            if (!item || !items.includes(item)) return;
            if (event instanceof KeyboardEvent && !['Enter', ' '].includes(event.key)) return;
            event.preventDefault();
            const open = item.classList.contains('is-active');
            if (open && el.dataset.allowAllClosed !== 'true' && items.filter(i => i.classList.contains('is-active')).length === 1) return;
            if (el.dataset.multiExpand !== 'true') items.forEach(i => i.classList.remove('is-active'));
            item.classList.toggle('is-active', !open);
            sync();
        };
        sync();
        el.addEventListener('click', click);
        el.addEventListener('keydown', click);
        cleanup(() => { el.removeEventListener('click', click); el.removeEventListener('keydown', click); });
    });

    Alpine.directive('ab-tooltip', (el, _directive, { cleanup }) => {
        const text = el.title;
        if (!text) return;
        el.removeAttribute('title');
        el.tabIndex = el.tabIndex < 0 ? 0 : el.tabIndex;
        const tip = document.createElement('div');
        tip.id = `${el.id}-tip`;
        tip.className = 'ab-tooltip';
        tip.setAttribute('role', 'tooltip');
        tip.textContent = text;
        tip.hidden = true;
        const dialog = el.closest('dialog');
        (dialog || document.body).append(tip);
        if (dialog) tip.style.position = 'absolute';
        el.setAttribute('aria-describedby', tip.id);
        const show = () => {
            tip.hidden = false;
            const rect = el.getBoundingClientRect();
            const origin = dialog?.getBoundingClientRect();
            const left = Math.max((origin?.left || 0) + 8, Math.min(rect.left, (origin?.right || innerWidth) - tip.offsetWidth - 8));
            const bottom = Math.min(origin?.bottom || innerHeight, innerHeight);
            const top = rect.bottom + 8 + tip.offsetHeight > bottom ? rect.top - tip.offsetHeight - 8 : rect.bottom + 8;
            tip.style.left = `${left - (origin?.left || 0) + (dialog?.scrollLeft || 0) - (dialog?.clientLeft || 0)}px`;
            tip.style.top = `${Math.max((origin?.top || 0) + 8, top) - (origin?.top || 0) + (dialog?.scrollTop || 0) - (dialog?.clientTop || 0)}px`;
        };
        const hide = () => { tip.hidden = true; };
        el.addEventListener('mouseenter', show);
        el.addEventListener('focus', show);
        el.addEventListener('mouseleave', hide);
        el.addEventListener('blur', hide);
        const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') hide(); };
        document.addEventListener('keydown', escape);
        cleanup(() => { tip.remove(); document.removeEventListener('keydown', escape); });
    });

    document.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : null;
        const trigger = target?.closest<HTMLElement>('[data-open], [data-toggle], [data-close]');
        if (!trigger) return;
        if (trigger.hasAttribute('data-close')) {
            const modal = trigger.closest<HTMLElement>('[data-dialog]');
            if (modal) { event.preventDefault(); UI.close(modal); }
        } else {
            const modal = document.getElementById(trigger.dataset.open || trigger.dataset.toggle || '');
            if (modal?.hasAttribute('data-dialog')) { event.preventDefault(); UI.open(modal); }
        }
    });
    document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && dialogState().active.length) UI.close('#' + dialogState().active.at(-1));
    });
    enhance();
    // Alpine initializes added directives itself. Only enhance genuinely new fragments.
    const enhancedFragments = new WeakSet<Node>();
    new MutationObserver(records => {
        for (const record of records) for (const node of record.addedNodes) {
            if (node instanceof HTMLElement && node.isConnected && !enhancedFragments.has(node)) {
                enhancedFragments.add(node);
                enhance(node);
            }
        }
    }).observe(document.body, { childList: true, subtree: true });
    // Alpine and older scripts update status classes after initial render.
    new MutationObserver(records => {
        for (const record of records) if (record.target instanceof HTMLElement && record.target.matches('.button, .callout, .label')) {
            enhanceAppearance(record.target);
        }
    }).observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });
});
